import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Chip,
  Avatar,
  Divider,
  useTheme,
  Button,
  InputAdornment,
  TextField
} from "@mui/material";
import { useEffect, useState } from "react";
import { getAllStockReceipts } from "../api/adminStockApi";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ReceiptIcon from "@mui/icons-material/Receipt";
import InventoryIcon from "@mui/icons-material/Inventory";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

type StockReceipt = {
  id: number;
  order_number: string;
  invoice_number?: string;
  delivery_date?: string;
  created_at: string;
  total_cost: string;
  supplier_name: string;
  received_by: string;
};

const StockReceiptListPage = () => {
  const [receipts, setReceipts] = useState<StockReceipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  useEffect(() => {
    const fetchReceipts = async () => {
      try {
        const data = await getAllStockReceipts();
        setReceipts(data);
      } catch (err) {
        console.error("Failed to fetch stock receipts:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchReceipts();
  }, []);

  const filteredReceipts = receipts.filter(receipt =>
    receipt.supplier_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    receipt.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (receipt.invoice_number && receipt.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={isMobile ? 2 : 3}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <ReceiptIcon color="primary" fontSize="large" />
        <Typography variant="h5" fontWeight="bold">
          Stock Receipts
        </Typography>
        <Chip 
          label={`${receipts.length} total`} 
          color="primary" 
          variant="outlined"
          size="small"
          sx={{ ml: 'auto' }}
        />
      </Stack>

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search receipts..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      {isMobile ? (
        <Stack spacing={2}>
          {filteredReceipts.map((receipt) => (
            <Card
              key={receipt.id}
              variant="outlined"
              sx={{
                borderRadius: 2,
                boxShadow: theme.shadows[1],
                '&:hover': {
                  boxShadow: theme.shadows[3],
                  borderColor: theme.palette.primary.main
                },
                transition: 'all 0.2s ease'
              }}
              onClick={() => navigate(`/stock-receipts/${receipt.id}`)}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Typography variant="subtitle1" fontWeight="bold" noWrap>
                    {receipt.supplier_name}
                  </Typography>
                  <Chip 
                    label={`#${receipt.order_number}`} 
                    size="small" 
                    color="primary"
                  />
                </Stack>

                <Divider sx={{ my: 1 }} />

                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <InventoryIcon color="action" fontSize="small" />
                    <Typography variant="body2">
                      {receipt.invoice_number ? `Invoice: ${receipt.invoice_number}` : 'No invoice'}
                    </Typography>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <AttachMoneyIcon color="action" fontSize="small" />
                    <Typography variant="body2" fontWeight="medium">
                      Total: ${parseFloat(receipt.total_cost).toFixed(2)}
                    </Typography>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarTodayIcon color="action" fontSize="small" />
                    <Typography variant="body2">
                      {new Date(receipt.created_at).toLocaleDateString()}
                    </Typography>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PersonIcon color="action" fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      Received by: {receipt.received_by}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Paper 
          variant="outlined" 
          sx={{ 
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: theme.shadows[1]
          }}
        >
          <Table>
            <TableHead sx={{ backgroundColor: theme.palette.grey[100] }}>
              <TableRow>
                <TableCell>Supplier</TableCell>
                <TableCell>Order #</TableCell>
                <TableCell>Invoice #</TableCell>
                <TableCell align="right">Total Cost</TableCell>
                <TableCell>Received Date</TableCell>
                <TableCell>Received By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReceipts.map((receipt) => (
                <TableRow
                  key={receipt.id}
                  hover
                  onClick={() => navigate(`/stock-receipts/${receipt.id}`)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:last-child td': { borderBottom: 0 }
                  }}
                >
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                        {receipt.supplier_name.charAt(0)}
                      </Avatar>
                      <Typography>{receipt.supplier_name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={`#${receipt.order_number}`} 
                      size="small" 
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    {receipt.invoice_number || (
                      <Typography color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="medium">
                      ${parseFloat(receipt.total_cost).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {new Date(receipt.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Typography>{receipt.received_by}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {filteredReceipts.length === 0 && (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          p={4}
          textAlign="center"
        >
          <InventoryIcon color="disabled" fontSize="large" />
          <Typography variant="h6" color="text.secondary" mt={2}>
            No stock receipts found
          </Typography>
          {searchTerm && (
            <Button 
              variant="text" 
              color="primary" 
              onClick={() => setSearchTerm('')}
              sx={{ mt: 1 }}
            >
              Clear search
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StockReceiptListPage;